/**
 * FSRS-5 spaced-repetition scheduler (the algorithm used by Anki), without interval fuzz so
 * that scheduling is deterministic and testable. Field names mirror the `cards` table.
 *
 * Reference: https://github.com/open-spaced-repetition/fsrs4anki/wiki/The-Algorithm
 */
import { MS_PER_DAY } from './date.ts';

export const Rating = { Again: 1, Hard: 2, Good: 3, Easy: 4 } as const;
export type Rating = (typeof Rating)[keyof typeof Rating];
export const RATINGS: readonly Rating[] = [Rating.Again, Rating.Hard, Rating.Good, Rating.Easy];

export const CardState = { New: 0, Learning: 1, Review: 2, Relearning: 3 } as const;
export type CardState = (typeof CardState)[keyof typeof CardState];

export interface SchedulingFields {
  due: Date;
  stability: number;
  difficulty: number;
  elapsed_days: number;
  scheduled_days: number;
  reps: number;
  lapses: number;
  state: CardState;
  last_review: Date | null;
}

export interface ReviewLogEntry {
  rating: Rating;
  /** State of the card before this review. */
  state: CardState;
  elapsed_days: number;
  scheduled_days: number;
  reviewed_at: Date;
}

export interface FsrsParams {
  w: readonly number[];
  requestRetention: number;
  maximumInterval: number;
}

/** FSRS-5 default weights. */
export const DEFAULT_WEIGHTS: readonly number[] = [
  0.40255, 1.18385, 3.173, 15.69105, 7.1949, 0.5345, 1.4604, 0.0046, 1.54575, 0.1192, 1.01925,
  1.9395, 0.11, 0.29605, 2.2698, 0.2315, 2.9898, 0.51655, 0.6621,
];

export const DEFAULT_PARAMS: FsrsParams = {
  w: DEFAULT_WEIGHTS,
  requestRetention: 0.9,
  maximumInterval: 36_500,
};

const DECAY = -0.5;
const FACTOR = 19 / 81; // chosen so that R(S, S) = 0.9
const MINUTE = 60_000;

/** Short-term steps (minutes) used while a card is in (re)learning. */
const LEARNING_STEPS = { again: 1, hard: 5, good: 10 } as const;
const RELEARNING_STEP_MINUTES = 10;

export function newCardFields(now: Date): SchedulingFields {
  return {
    due: now,
    stability: 0,
    difficulty: 0,
    elapsed_days: 0,
    scheduled_days: 0,
    reps: 0,
    lapses: 0,
    state: CardState.New,
    last_review: null,
  };
}

export function retrievability(elapsedDays: number, stability: number): number {
  if (stability <= 0) return 0;
  return Math.pow(1 + (FACTOR * elapsedDays) / stability, DECAY);
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

const weight = (p: FsrsParams, i: number): number => {
  const value = p.w[i];
  if (value === undefined) throw new Error(`FSRS weight w[${i}] is missing`);
  return value;
};

function initStability(p: FsrsParams, rating: Rating): number {
  return Math.max(weight(p, rating - 1), 0.1);
}

function initDifficulty(p: FsrsParams, rating: Rating): number {
  return weight(p, 4) - Math.exp(weight(p, 5) * (rating - 1)) + 1;
}

function nextDifficulty(p: FsrsParams, d: number, rating: Rating): number {
  const delta = -weight(p, 6) * (rating - 3);
  const damped = d + (delta * (10 - d)) / 9; // linear damping (FSRS-5)
  const target = initDifficulty(p, Rating.Easy);
  return clamp(weight(p, 7) * target + (1 - weight(p, 7)) * damped, 1, 10);
}

function recallStability(p: FsrsParams, d: number, s: number, r: number, rating: Rating): number {
  const hardPenalty = rating === Rating.Hard ? weight(p, 15) : 1;
  const easyBonus = rating === Rating.Easy ? weight(p, 16) : 1;
  return (
    s *
    (1 +
      Math.exp(weight(p, 8)) *
        (11 - d) *
        Math.pow(s, -weight(p, 9)) *
        (Math.exp((1 - r) * weight(p, 10)) - 1) *
        hardPenalty *
        easyBonus)
  );
}

function forgetStability(p: FsrsParams, d: number, s: number, r: number): number {
  const value =
    weight(p, 11) *
    Math.pow(d, -weight(p, 12)) *
    (Math.pow(s + 1, weight(p, 13)) - 1) *
    Math.exp((1 - r) * weight(p, 14));
  return Math.min(value, s);
}

function shortTermStability(p: FsrsParams, s: number, rating: Rating): number {
  return s * Math.exp(weight(p, 17) * (rating - 3 + weight(p, 18)));
}

export function nextIntervalDays(p: FsrsParams, stability: number): number {
  const raw = (stability / FACTOR) * (Math.pow(p.requestRetention, 1 / DECAY) - 1);
  return clamp(Math.round(raw), 1, p.maximumInterval);
}

function wholeDaysBetween(from: Date, to: Date): number {
  return Math.max(0, Math.floor((to.getTime() - from.getTime()) / MS_PER_DAY));
}

export interface ReviewResult {
  card: SchedulingFields;
  log: ReviewLogEntry;
}

/** Applies one review with `rating` at `now` and returns the updated card and its log entry. */
export function review(
  card: SchedulingFields,
  rating: Rating,
  now: Date,
  params: FsrsParams = DEFAULT_PARAMS,
): ReviewResult {
  const elapsed = card.last_review ? wholeDaysBetween(card.last_review, now) : 0;
  const log: ReviewLogEntry = {
    rating,
    state: card.state,
    elapsed_days: elapsed,
    scheduled_days: card.scheduled_days,
    reviewed_at: now,
  };
  const base = { ...card, elapsed_days: elapsed, last_review: now, reps: card.reps + 1 };
  const inMinutes = (m: number) => new Date(now.getTime() + m * MINUTE);
  const inDays = (d: number) => new Date(now.getTime() + d * MS_PER_DAY);

  const toReview = (stability: number, difficulty: number, days: number): SchedulingFields => ({
    ...base,
    stability,
    difficulty,
    state: CardState.Review,
    scheduled_days: days,
    due: inDays(days),
  });
  const toShortTerm = (
    stability: number,
    difficulty: number,
    state: CardState,
    minutes: number,
  ): SchedulingFields => ({
    ...base,
    stability,
    difficulty,
    state,
    scheduled_days: 0,
    due: inMinutes(minutes),
  });

  switch (card.state) {
    case CardState.New: {
      const s = initStability(params, rating);
      const d = clamp(initDifficulty(params, rating), 1, 10);
      const result =
        rating === Rating.Again
          ? toShortTerm(s, d, CardState.Learning, LEARNING_STEPS.again)
          : rating === Rating.Hard
            ? toShortTerm(s, d, CardState.Learning, LEARNING_STEPS.hard)
            : rating === Rating.Good
              ? toShortTerm(s, d, CardState.Learning, LEARNING_STEPS.good)
              : toReview(s, d, nextIntervalDays(params, s));
      return { card: result, log };
    }
    case CardState.Learning:
    case CardState.Relearning: {
      const s = shortTermStability(params, card.stability, rating);
      const d = nextDifficulty(params, card.difficulty, rating);
      if (rating === Rating.Again) {
        return { card: toShortTerm(s, d, card.state, LEARNING_STEPS.hard), log };
      }
      if (rating === Rating.Hard) {
        return { card: toShortTerm(s, d, card.state, LEARNING_STEPS.good), log };
      }
      const goodDays = nextIntervalDays(params, s);
      if (rating === Rating.Good) return { card: toReview(s, d, goodDays), log };
      const easyStability = shortTermStability(params, card.stability, Rating.Easy);
      const easyDays = Math.max(nextIntervalDays(params, easyStability), goodDays + 1);
      return { card: toReview(easyStability, d, easyDays), log };
    }
    case CardState.Review: {
      const r = retrievability(elapsed, card.stability);
      const d = nextDifficulty(params, card.difficulty, rating);
      if (rating === Rating.Again) {
        const s = forgetStability(params, card.difficulty, card.stability, r);
        const lapsed = toShortTerm(s, d, CardState.Relearning, RELEARNING_STEP_MINUTES);
        return { card: { ...lapsed, lapses: card.lapses + 1 }, log };
      }
      const sHard = recallStability(params, card.difficulty, card.stability, r, Rating.Hard);
      const sGood = recallStability(params, card.difficulty, card.stability, r, Rating.Good);
      const sEasy = recallStability(params, card.difficulty, card.stability, r, Rating.Easy);
      let hardDays = nextIntervalDays(params, sHard);
      let goodDays = nextIntervalDays(params, sGood);
      hardDays = Math.min(hardDays, goodDays);
      goodDays = Math.max(goodDays, hardDays + 1);
      const easyDays = Math.max(nextIntervalDays(params, sEasy), goodDays + 1);
      if (rating === Rating.Hard) return { card: toReview(sHard, d, hardDays), log };
      if (rating === Rating.Good) return { card: toReview(sGood, d, goodDays), log };
      return { card: toReview(sEasy, d, easyDays), log };
    }
  }
}

/** Preview of every rating, for showing the next interval on the rating buttons. */
export function previewAll(
  card: SchedulingFields,
  now: Date,
  params: FsrsParams = DEFAULT_PARAMS,
): Record<Rating, SchedulingFields> {
  return {
    [Rating.Again]: review(card, Rating.Again, now, params).card,
    [Rating.Hard]: review(card, Rating.Hard, now, params).card,
    [Rating.Good]: review(card, Rating.Good, now, params).card,
    [Rating.Easy]: review(card, Rating.Easy, now, params).card,
  };
}

/** Short Russian label for the time until `due`, e.g. "10 мин", "3 д", "2 мес". */
export function formatInterval(now: Date, due: Date): string {
  const minutes = Math.max(1, Math.round((due.getTime() - now.getTime()) / MINUTE));
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ч`;
  const days = Math.round(minutes / (60 * 24));
  if (days < 30) return `${days} д`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} мес`;
  return `${Math.round((days / 365) * 10) / 10} г`;
}
